import React, { useState } from 'react';
import { FiX, FiBookOpen, FiCopy, FiDownload, FiRefreshCw, FiCheck } from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './LitReviewModal.css';

const LENGTH_OPTIONS = [
  { value: 'short',  label: 'Short',  hint: '~500 words' },
  { value: 'medium', label: 'Medium', hint: '~900 words' },
  { value: 'long',   label: 'Long',   hint: '~1,400 words' },
];

const LitReviewModal = ({ isOpen, onClose, collection }) => {
  const [focus, setFocus] = useState('');
  const [length, setLength] = useState('medium');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { markdown, paper_count, word_count }
  const [copied, setCopied] = useState(false);

  if (!isOpen || !collection) return null;

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/collections/${collection.id}/literature-review`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ focus: focus.trim() || null, length }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Generation failed');
      }
      setResult(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result.markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!result) return;
    const blob = new Blob([result.markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${collection.name.replace(/\s+/g, '_')}_lit_review.md`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleReset = () => {
    setResult(null);
    setError('');
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="lr-overlay" onClick={handleOverlayClick}>
      <div className="lr-modal">

        {/* Header */}
        <div className="lr-header">
          <div className="lr-title">
            <FiBookOpen className="lr-title-icon" />
            <div>
              <h2>Generate Literature Review</h2>
              <p className="lr-subtitle">{collection.name}</p>
            </div>
          </div>
          <button className="lr-close" onClick={onClose} aria-label="Close"><FiX /></button>
        </div>

        {/* Body */}
        {!result ? (
          <div className="lr-body">
            {/* Focus field */}
            <div className="lr-field">
              <label className="lr-label">Focus or angle <span className="lr-optional">(optional)</span></label>
              <input
                className="lr-input"
                type="text"
                placeholder="e.g. emphasise methodology, focus on 2020–2025, highlight clinical applications…"
                value={focus}
                onChange={e => setFocus(e.target.value)}
                disabled={generating}
                maxLength={200}
              />
            </div>

            {/* Length selector */}
            <div className="lr-field">
              <label className="lr-label">Target length</label>
              <div className="lr-length-options">
                {LENGTH_OPTIONS.map(opt => (
                  <label key={opt.value} className={`lr-length-option${length === opt.value ? ' selected' : ''}`}>
                    <input
                      type="radio"
                      name="lr-length"
                      value={opt.value}
                      checked={length === opt.value}
                      onChange={() => setLength(opt.value)}
                      disabled={generating}
                    />
                    <span className="lr-length-label">{opt.label}</span>
                    <span className="lr-length-hint">{opt.hint}</span>
                  </label>
                ))}
              </div>
            </div>

            {error && <p className="lr-error">{error}</p>}

            <button
              className="lr-generate-btn"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? (
                <>
                  <span className="lr-spinner" />
                  Generating… this takes 20–40 seconds
                </>
              ) : (
                <>
                  <FiBookOpen /> Generate Literature Review
                </>
              )}
            </button>

            <p className="lr-disclaimer">
              Powered by AI · Always verify citations before submitting
            </p>
          </div>
        ) : (
          <div className="lr-result-body">
            {/* Result toolbar */}
            <div className="lr-result-toolbar">
              <span className="lr-result-meta">
                {result.word_count.toLocaleString()} words · {result.paper_count} papers
              </span>
              <div className="lr-result-actions">
                <button className="lr-action-btn" onClick={handleCopy} title="Copy to clipboard">
                  {copied ? <FiCheck /> : <FiCopy />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <button className="lr-action-btn" onClick={handleDownload} title="Download as Markdown">
                  <FiDownload /> Download .md
                </button>
                <button className="lr-action-btn secondary" onClick={handleReset} title="Generate again">
                  <FiRefreshCw /> Regenerate
                </button>
              </div>
            </div>

            {/* Markdown preview */}
            <pre className="lr-markdown-preview">{result.markdown}</pre>

            <p className="lr-disclaimer">
              AI-generated · Verify all citations before use
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LitReviewModal;
